import { useEffect, useState } from 'react';

// Define interface for config object
export interface OidcConfig {
  url: string;
  clientId: string;
}

const useConfig = (): [OidcConfig | null, null, boolean] => {
  const [config, setConfig] = useState<OidcConfig | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    async function getProviders() {
      setIsLoading(true);
      try {
        // First priority: Check session storage
        const sessionConfig = sessionStorage.getItem('config');
        if (sessionConfig) {
          const parsedConfig = JSON.parse(sessionConfig);
          setConfig(parsedConfig);
          setIsLoading(false);
          return;
        }

        // Second priority: Use environment variables
        const envConfig: OidcConfig = {
          url: process.env.REACT_APP_KEYCLOAK_URL || '',
          clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || '',
        };

        if (envConfig.url && envConfig.clientId) {
          setConfig(envConfig);
          sessionStorage.setItem('config', JSON.stringify(envConfig));
          setIsLoading(false);
          return;
        }

        // Third priority: Fetch from API
        const response = await fetch(
          `${process.env.REACT_APP_BASEURL}/api/Provider`,
          {
            method: 'GET',
            credentials: 'include',
          }
        );
        const data = await response.json();

        const oidcConfig: OidcConfig = {
          url: data?.Url,
          clientId: data?.ClientId,
        };

        setConfig(oidcConfig);
        sessionStorage.setItem('config', JSON.stringify(oidcConfig));
      } catch (error) {
        console.error('Error initializing OIDC config:', error);
      } finally {
        setIsLoading(false);
      }
    }

    getProviders();
  }, []);

  // Return config and loading state, but no Keycloak instance
  return [config, null, isLoading];
};

export default useConfig;
